<script lang="ts">
	import { Button, SocialLoginButton, EmailLoginButton } from '$lib/components/atoms/Button';
	import { goto } from '$app/navigation';
	import { page } from '$app/stores';
	import { useOktaStore } from '$lib/utils/auth-store';
	import { GtmStore } from '$lib/utils/gtm-store';
	import classNames from 'classnames';
	import { createEventDispatcher, tick } from 'svelte';
	import { PUBLIC_OKTA_GOOGLE_IDP_ID, PUBLIC_OKTA_FACEBOOK_IDP_ID } from '$env/static/public';
	import { browser } from '$app/environment';
	import { isWebView } from '$lib/utils/browser-utils';


	const dispatch = createEventDispatcher();

	const authStore = useOktaStore();
	const userInfo = authStore.userInfo;

	export let socialAction = 'Registrati';
	export let socialInline: boolean = false;
	export let useDecoratedTitle: boolean = false;
	export let showTitle = true;
	export let isRegistering: boolean = false;
	export let options: {
		socialSection?: string | boolean;
		mainSection?: string | boolean;
		loginSection?: string | boolean;
		socialOnly?: string;
		selectedEmail?: string;
	};

	let genericErrors: string[] = [];

	if (browser && $page.url.searchParams.get('error')) {
		const error = $page.url.searchParams.get('error');

		if (error !== 'oautherror-user-canceled-the-social-login-request') {
			genericErrors.push('Si è verificato un errore durante l\'accesso. Riprova. Se il problema persiste, contatta il nostro servizio clienti.');
		}
	}

	/**
	 * Perform the social sign in
	 * @param idp The IDP unique code
	 * @param redirect True to redirect, false to use popup
	 */
	const socialSignIn = async (
		provider: 'google' | 'facebook',
		idp: string,
		redirect: boolean,
		errorMessage?: string
	) => {
		const callback = browser ? ($page.url.searchParams.get('callback') as string) : '';
		const cta = browser ? ($page.url.searchParams.get('cta') as string) : '';
		isRegistering = true;

		genericErrors = [];

		try {
			const res = await authStore.signInWithIdp(idp, redirect, provider, callback, cta);
			dispatch('social-sign-in', $userInfo);
		} catch (e) {
			console.error(e);
		} finally {
			isRegistering = false;
		}
	};

	const onSocialLogin = async (type: 'google' | 'facebook') => {
		const idp = {
			google: PUBLIC_OKTA_GOOGLE_IDP_ID,
			facebook: PUBLIC_OKTA_FACEBOOK_IDP_ID
		};

		await socialSignIn(
			type,
			idp[type],
			false,
			'Utilizza la registrazione tradizionale oppure accedi nuovamente tramite la registrazione social'
		);
	};

	const onClick = async (type: 'google' | 'facebook' | 'register' | 'login') => {
		const searchParams = new URLSearchParams(window.location.search);
		const callback = searchParams.get('callback');
		const cta = searchParams.get('cta');

		switch (type) {
			case 'register':
				goto('/registrati/form' + (callback ? `?callback=${callback}` : cta ? `?cta=${cta}` : ''), {
					replaceState: true
				});
				break;
			case 'login':
				goto('/login' + (callback ? `?callback=${callback}` : cta ? `?cta=${cta}` : ''), {
					replaceState: true
				});
				break;
			case 'google':
				await onSocialLogin('google');
				break;
			case 'facebook':
				await onSocialLogin('facebook');
				break;
		}
	};
</script>

<div class="container">
	{#if options.socialSection}
		{#if !isWebView() && showTitle}
			{#if useDecoratedTitle}
				<h3 class="decorated"><span>{options.socialSection}</span></h3>
			{:else}
				<h1 class="fs-2 md:fs-4">{options.socialSection}</h1>
			{/if}
		{/if}

		{#if genericErrors && genericErrors.length > 0}
			<div class="rounded-md bg-red-50 mt-8 p-4">
				<div class="flex">
					<div class="shrink-0">
						<svg class="size-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" data-slot="icon">
							<path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z" clip-rule="evenodd" />
						</svg>
					</div>
					<div class="text-sm text-red-700">
						<ul role="list" class="space-y-1 pl-5">
							{#each genericErrors as error}
								<li>{error}</li>
							{/each}
						</ul>
					</div>
				</div>
			</div>
		{/if}

		{#if !isWebView()}
			<div
				class={classNames('social-button-container', {
					'text-center': useDecoratedTitle,
					'social-inline': socialInline,
					'mt-8 mb-3': options.socialOnly,
					'my-8': !options.socialOnly
				})}
			>
				{#if options.socialOnly && options.socialOnly == 'google'}
					<SocialLoginButton social="google" on:click={() => onClick('google')}>
						<div class="flex flex-col">
							{socialAction} con Google
							{#if options.selectedEmail}<small class="leading-none">{options.selectedEmail}</small
								>{/if}
						</div>
					</SocialLoginButton>
				{:else if options.socialOnly && options.socialOnly == 'facebook'}
					<SocialLoginButton social="facebook" on:click={() => onClick('facebook')}>
						<div class="flex flex-col">
							{socialAction} con Facebook
							{#if options.selectedEmail}<small class="leading-none">{options.selectedEmail}</small
								>{/if}
						</div>
					</SocialLoginButton>
				{:else}
					<SocialLoginButton social="google" on:click={() => onClick('google')}
						>{socialAction} con Google</SocialLoginButton
					>
					<SocialLoginButton social="facebook" on:click={() => onClick('facebook')}
						>{socialAction} con Facebook</SocialLoginButton
					>
				{/if}
			</div>
		{/if}
	{/if}

	{#if options.mainSection}
		{#if !isWebView()}
			{#if useDecoratedTitle}
				<h3 class="decorated"><span>{options.mainSection}</span></h3>
			{:else}
				<h1 class="fs-2 md:fs-4">{options.mainSection}</h1>
			{/if}
		{/if}

		<div
			class={classNames('my-8', 'email-button-container', {
				'text-center': useDecoratedTitle,
				'social-inline': socialInline
			})}
		>
			<EmailLoginButton on:click={() => onClick('register')}>
				Registrati con la tua mail
			</EmailLoginButton>
		</div>
	{/if}

	{#if options.loginSection}
		{#if useDecoratedTitle}
			<h3 class="decorated"><span>{options.loginSection}</span></h3>
		{:else}
			<h1 class="fs-2 md:fs-4">{options.loginSection}</h1>
		{/if}

		<div
			class={classNames('my-8', {
				'text-center': useDecoratedTitle
			})}
		>
			<Button on:click={() => onClick('login')}>Accedi</Button>
		</div>
	{/if}
</div>

<style type="text/css" lang="scss" scoped>
	.social-button-container,
	.email-button-container {
		@apply grid grid-cols-1 gap-2;

		&.social-inline {
			@apply grid grid-cols-1 md:grid-cols-2 gap-8;
		}
	}

	h3 {
		@apply text-lg;
	}
	/* headlines with lines */
	.decorated {
		@apply overflow-hidden;
		@apply text-center;
	}
	.decorated > span {
		@apply relative;
		@apply inline-block;
	}
	.decorated > span:before,
	.decorated > span:after {
		content: '';
		@apply absolute;
		@apply top-1/2;
		@apply border-b border-gray-300;
		@apply mx-0 my-5;
		width: 591px; /* half of limiter*/
	}
	.decorated > span:before {
		@apply right-full;
	}
	.decorated > span:after {
		@apply left-full;
	}
</style>
